<script lang="ts" setup generic="TItem extends TypeAheadItem">
import { DropdownComp, IconComp, SearchBox, RouterLink } from '@/components';
import { TypeAheadItem, SearchRequest, TypeAheadResponse } from '@/objects/generated';
import { watch, ref, computed, nextTick, type Component, onMounted } from 'vue';
import { useHelper } from '@/composeables';
import { featureIcons } from '@/.';
import { type TypeAheadDataSource, type TypeAheadExpose } from '@/components/Typeahead';
import { arrayToRingIterator, CreateRegexpSearchPatternForSearchType, convertToDebouncedFunction } from '@/utilities';
import type { SearchType } from '@/objects/generated/enums/SearchType';
import type { IDropDownExposed } from '../Basic/BootstrapWrappers/DropdownComp/IDropDownExposed';
import type { IEmitBlur, IEmitFocus } from '@/components/Interfaces';

const props = withDefaults(
    defineProps<{
        /**
         * The placeholder text for the input field. Should be a translation key
         * @default ''
         */
        placeholder?: string;

        /**
         * The minimum length of the input before we start searching
         * @default 3
         */
        minimumLength?: number;

        /**
         * The maximum length of the cache
         * @default 100
         */
        maxCacheLength?: number;

        /**
         * The number of items to display pr datasource
         * @default 50
         */
        displayItems?: number;

        /**
         * The timeout before we start searching in ms
         * @default 300
         */
        defererTimeout?: number;

        /**
         * The datasources to search from
         */
        datasources: TypeAheadDataSource[];

        /**
         * If we should prettyfy the output
         * @default false
         */
        prettify?: boolean;

        /**
         * If we should ignore the minvalue for number of character on integer values. i.e searching for IDs
         * @default false
         */
        ignoreMinvalueOnInteger?: boolean;

        /**
         * Render component for the item
         */
        displayItemComponent?: Component<{ item: TItem }>;

        /**
         * If we should display the selected text in the input field
         * @default false
         */
        displaySelectedText?: boolean;

        /**
         * If we should hide the nomatch test
         * @default false
         */
        hideNomatchText?: boolean;

        /**
         * If the dropdown should autoresize to the input field width
         * @default false
         */
        autoresize?: boolean;
    }>(),
    {
        placeholder: '',
        minimumLength: 3,
        maxCacheLength: 100,
        defererTimeout: 300,
        prettify: false,
        displayItems: 50,
        ignoreMinvalueOnInteger: false,
        displaySelectedText: false,
        hideNomatchText: false,
    },
);

// The input field
const input = ref<HTMLInputElement | null>(null);

// The wrapper element for the typeahead
const typeaheadwrapper = ref<HTMLElement | null>(null);

// The value of the input field
const value = defineModel<string | null>({ required: true });

// The dropdown
const dropdown = ref<IDropDownExposed | null>(null);

// Every renderered item element
const itemElements = ref<(typeof RouterLink)[]>([]);

// const keyboardActiveElementClass = 'eq-form-focus';

// Check if the minimum length is met
const isMinimumMet = computed(() => {
    // we need to check if the value is an integer
    let isInteger = /[0-9]+/.test(value.value ?? '');

    // if it is an integer we always return true if ignoreMinvalueOnInteger is true
    if (isInteger && props.ignoreMinvalueOnInteger) {
        return true;
    }

    return (value.value?.length ?? 0) >= props.minimumLength;
});

// The datasources are ref'ed so vue detects changes.
const datasources = ref<TypeAheadDataSource[]>(props.datasources);

// Make sure MaxItems has a value for each dataset
datasources.value.forEach((ds) => (ds.MaxItems = ds.MaxItems ?? props.displayItems));

// Total number of results across all datasources
const sumResultCount = computed(() => datasources.value.reduce((acc, x) => acc + (x.ActiveResult?.Items.length ?? 0), 0));

// If there is any sticky items in any of the datasources
const hasStickyItems = computed(() => datasources.value.some((x) => x.StickyItems && x.StickyItems.length > 0));

// We need a non reactive snapshot of the search value so we can mark the latest search, and not change the mark until we are done searching again
let searchValueSnapshot = '';

// Define the emits
const emits = defineEmits<
    {
        (e: 'itemSelected', item: TItem): void;
    } & IEmitBlur &
        IEmitFocus
>();

// The currently keyboard selected item
const keyboardActiveItem = ref<TItem | null>(null);

// This is a looping "iterator" with prev and next over all the currently visible items.
const allItemsIterator = computed(() =>
    arrayToRingIterator(
        (datasources.value.flatMap((x) => x.StickyItems) ?? [])
            .concat(datasources.value.flatMap((x) => x.ActiveResult?.Items ?? []))
            .filter((x) => x != null) as TItem[],
    ),
);

/**
 * Handle keyboard navigation with arrow keys
 * We have to do this since we have an input in the dropdown, and that disables bootstraps native keyboard navigation (bootstrap uses focus anyway, and we want to keep the focus in the inputbox (or do we?))
 * We are using the .eq-form-focus class to "simulate" focus on the element
 * TODO: This should really be a feature of the dropdown component, not the typeahead so we can reuse it the next time we have a dropdown with an input field
 * There might be a problem with accessing the allItemsIterator from the dropdown component if we are using the slots tho...
 * @param e
 */
const keyDown = (e: KeyboardEvent) => {
    // If we are not keydown, keyup we will return
    if (e.code !== 'ArrowDown' && e.code !== 'ArrowUp' && e.code !== 'Enter' && e.code !== 'Escape') {
        return;
    }

    if (!dropdown.value) {
        return;
    }

    if (e.code === 'Escape') {
        hide();
        return;
    }

    e.preventDefault();

    switch (e.code) {
        case 'ArrowDown':
            keyboardActiveItem.value = allItemsIterator.value.next();

            if (keyboardActiveItem.value === null) {
                // If we are at the end of the list and press down, we want to loop around to the top. Same for up but reversed. We need to do this because of the sticky items, which are in a different list than the search results, and we want to be able to navigate through them seamlessly.
                keyboardActiveItem.value = allItemsIterator.value.next();
            }
            break;
        case 'ArrowUp':
            keyboardActiveItem.value = allItemsIterator.value.prev();

            if (keyboardActiveItem.value === null) {
                // If we are at the end of the list and press down, we want to loop around to the top. Same for up but reversed. We need to do this because of the sticky items, which are in a different list than the search results, and we want to be able to navigate through them seamlessly.
                keyboardActiveItem.value = allItemsIterator.value.prev();
            }

            break;
        case 'Enter':
            if (keyboardActiveItem.value) {
                handleItem(e, keyboardActiveItem.value);
            }
            break;
    }

    const itemElementIdx = itemElements.value.findIndex((x) => x.$el.classList.contains('eq-form-focus'));
    if (itemElementIdx === -1) {
        return;
    }

    let nextItemElement = null;

    // The next element should be either 2 elements down, if that is not possible, then one element down if that is not possible either then we loop around to the top. Same logic for up but reversed. We need to do this because of the sticky items, which are in a different list than the search results, and we want to be able to navigate through them seamlessly.
    let numberOfItemsLeft = itemElements.value.length - itemElementIdx - 1;

    if (e.code == 'ArrowUp') {
        numberOfItemsLeft = itemElementIdx;
    }

    if (e.code == 'ArrowDown') {
        if (numberOfItemsLeft >= 2) {
            nextItemElement = itemElements.value[itemElementIdx + 2];
        } else if (numberOfItemsLeft === 1) {
            nextItemElement = itemElements.value[itemElementIdx + 1];
        } else {
            nextItemElement = itemElements.value[0];
        }
    } else if (e.code == 'ArrowUp') {
        if (itemElementIdx >= 2) {
            nextItemElement = itemElements.value[itemElementIdx - 2];
        } else if (itemElementIdx === 1) {
            nextItemElement = itemElements.value[itemElementIdx - 1];
        } else {
            nextItemElement = itemElements.value[itemElements.value.length - 1];
        }
    }

    nextItemElement?.$el.scrollIntoView({
        block: 'nearest',
    });
};

// When the input field gets focus
const onFocus = (ev: Event) => {
    // If we have stickyitems we show the dropdown on focus, otherwise we wait until the user starts typing
    if (hasStickyItems.value) {
        // We add a small delay here because otherwise the dropdown will open and close immediately because of the focus and blur events when clicking on the input field, this is a bit of a hack but it works
        setTimeout(() => {
            show();
        }, 100);
    }

    emits('focus', ev);
};

// When the input field loses focus
const onBlur = (ev: Event) => {
    hide();

    emits('blur', ev);
};

// Helper property so we dont immidiately show the dropdown when we select an item and set the value of the input field of the selected item
let handledItem = false;

/**
 * Handle the item click.
 * @param item The item that was clicked
 */
const handleItem = (event: Event, item: TItem) => {
    emits('itemSelected', item); // TODO: Maybe this should emit just the item.Id instead of the whole item object
    hide();

    // Set the value of the input field to the selected item

    if (props.displaySelectedText) {
        handledItem = true;
        value.value = item.Text!;
    } else {
        handledItem = false;
        value.value = '';
    }

    useHelper().stopPropagation(event);

    return false;
};

/**
 * Prettyfy the output
 * This will capitalize the first letter of each word
 * @param text
 */
const prettifyOutput = (text: string) => {
    if (!props.prettify) {
        return text;
    }
    return text
        .split(' ')
        .map((x) => x.charAt(0).toUpperCase() + x.slice(1))
        .join(' ');
};

// Show the dropdown
const show = () => dropdown.value?.show();

// Hide the dropdown
const hide = () => dropdown.value?.hide();

/**
 * Will mark the search text in the text
 * @param text
 * @param request The request
 */
const markSearchText = (text: string, searchType: SearchType, query: string) => {
    if (!query) {
        return text;
    }

    let searchPattern = CreateRegexpSearchPatternForSearchType(query, searchType);

    return text.replace(searchPattern, (match) => `<mark>${match}</mark>`);
};

/**
 * Wrap the searching from datasources in a debounced function
 */
const debouncedSearch = convertToDebouncedFunction(async (query: string) => {
    // Take a snapshot of the search value
    searchValueSnapshot = query;

    // Reset the keyboard active item
    keyboardActiveItem.value = null;

    const promises: Promise<TypeAheadResponse>[] = [];

    datasources.value.forEach(async (ds) => {
        const request = SearchRequest.FromJsValues({
            Query: query,
            Maxitems: ds.MaxItems ?? props.displayItems,
            SearchType: ds.SearchType,
        });
        request.SearchType = ds.SearchType; // FUCK, the TypeAheadRequest constructor is expecting the SearchType enum as an int value, not the string.

        // Note: we clear the cache BEFORE we load the new items.
        // This might lead to the number of cached items exceeding maxCacheLength
        ds.ClearCacheIfFull(props.maxCacheLength);

        promises.push(
            ds.QueryCached(request).then((result) => {
                ds.ActiveResult = result;
                return result;
            }),
        );
    });

    await Promise.all(promises);

    show();
}, props.defererTimeout);

/**
 * This is where we handle the changes in the input field. And do the actual search
 * @param newvalue
 * @param oldvalue
 */
const handleInput = async (newvalue: string | null, oldvalue?: string | null) => {
    // Dont do anything if the value is the same, or if we just selected an item
    if (newvalue == null || newvalue === oldvalue || handledItem) {
        handledItem = false;
        return;
    }

    // Check if the newvalue is integer using regex
    let isInteger = /[0-9]+/.test(newvalue ?? '');

    // dont do anything if the minimum length is not met
    if ((newvalue?.length ?? 0) < props.minimumLength && !(isInteger && props.ignoreMinvalueOnInteger)) {
        return;
    }

    debouncedSearch(newvalue);
};

// Watch the modelValue changes
watch(
    value,
    (newVal, oldVal) => {
        if (newVal === null) {
            value.value = '';
        } else {
            handleInput(newVal, oldVal);
        }
    },
    { immediate: true },
);

// Exposed functions
defineExpose<TypeAheadExpose>({
    /**
     * Focus the input
     */
    focus: () => input.value?.focus(),
});

const dropdowMinimumWidth = ref<string | null>(null);

/**
 * Resize the dropdown to the input field width
 */
const resizeDropdown = () => {
    if (props.autoresize === true) {
        // Get the element size of the searchbox input
        const inputRect = typeaheadwrapper.value?.getBoundingClientRect();

        if (inputRect != null && inputRect.width > 0) {
            dropdowMinimumWidth.value = `${inputRect.width}px !important`;
        }
    }

    const menues = typeaheadwrapper.value?.querySelectorAll('.dropdown-menu');

    // We add a mousedown event listener to the dropdown menu to stopPropagation
    // Why is this neccessary? The typeahead works differently outside of the modal, if you press the scrollbar of the dropdown menu it does NOT trigger the blur event on the input field,
    // but in the modal it does trigger the blur event, which causes the dropdown to close immediately when you try to scroll. This is a workaround for that issue
    menues?.forEach((menu) => {
        menu.addEventListener('mousedown', (event) => {
            useHelper().stopPropagation(event);
        });
    });
};

onMounted(() => {
    resizeDropdown();
});
</script>
<template>
    <div
        class="typeahead"
        ref="typeaheadwrapper"
        v-if="value !== null"
    >
        <DropdownComp
            class="dropdown"
            ref="dropdown"
            autoClose="outside"
            @shown="resizeDropdown"
        >
            <template #trigger>
                <SearchBox
                    v-model="value"
                    :class="$attrs.class"
                    aria-expanded="false"
                    ref="input"
                    :placeholder="placeholder"
                    @focus="onFocus"
                    @blur="onBlur"
                    @keydown="keyDown"
                >
                    <span
                        class="input-group-text-inline d-flex align-items-center bg-transparent"
                        v-if="hasStickyItems"
                    >
                        <IconComp icon="fa-chevron-down" />
                    </span>
                </SearchBox>
            </template>
            <template #menu>
                <ul
                    class="dropdown-menu"
                    :style="{ minWidth: dropdowMinimumWidth ?? 'auto' }"
                >
                    <template v-if="!isMinimumMet && !hasStickyItems">
                        <li class="dropdown-item">
                            <div class="py-2">
                                <IconComp
                                    :icon="featureIcons.INFO"
                                    class="text-info me-2"
                                />
                                {{ $tm('general.minimumsearchnotmet', props.minimumLength) }}
                            </div>
                        </li>
                    </template>
                    <template v-else-if="sumResultCount === 0 && !props.hideNomatchText && !hasStickyItems">
                        <li class="dropdown-item">
                            <div class="py-2">
                                <IconComp
                                    :icon="featureIcons.INFO"
                                    class="text-info me-2"
                                />
                                {{ $tm('general.searchempty') }}
                            </div>
                        </li>
                    </template>
                    <template v-else>
                        <template
                            v-for="(datasource, j) in datasources"
                            :key="j"
                        >
                            <template v-if="datasource.StickyItems && datasource.StickyItems.length > 0">
                                <li
                                    class="border border-top-0 border-end-0 border-start-0 border-light-subtle"
                                    v-for="item in datasource.StickyItems"
                                    :key="item.ID"
                                >
                                    <!-- we use mousedown instead of click as this is handled before blur that hides menu -->
                                    <RouterLink
                                        ref="itemElements"
                                        :href="datasource.hrefGetter !== undefined ? datasource?.hrefGetter(item) : undefined"
                                        class="dropdown-item py-2 d-flex flex-row flex-nowrap"
                                        :class="{ 'eq-form-focus': keyboardActiveItem === item }"
                                        @mousedown="handleItem($event, item as TItem)"
                                    >
                                        <div
                                            class="flex-grow-1 flex-fill"
                                            v-html="markSearchText(prettifyOutput(item.Text!), datasource.SearchType, searchValueSnapshot)"
                                        ></div>
                                        <div
                                            v-if="item.Description"
                                            class="text-muted me-3"
                                            v-html="$tm(item.Description)"
                                        ></div>
                                        <div class="ms-auto flex-shrink-1 text-align-end">
                                            <IconComp
                                                icon="fa-thumbtack"
                                                class="opacity-50 fa-sm"
                                            />
                                        </div>
                                    </RouterLink>
                                </li>
                                <li
                                    class="mt-5 mb-3"
                                    v-if="sumResultCount > 0"
                                >
                                    <h6 class="display-4 ps-1 text-muted">{{ $tm('typeahead.searchresults') }}</h6>
                                </li>
                            </template>

                            <template v-if="datasource.ActiveResult && datasource.ActiveResult.Items.length > 0">
                                <li v-if="datasource.ResultHeaderText">
                                    <h6 class="ps-1">{{ $tm(datasource.ResultHeaderText) }}</h6>
                                    <hr />
                                </li>
                                <li
                                    class="border border-top-0 border-end-0 border-start-0 border-light-subtle"
                                    v-for="item in datasource.ActiveResult.Items"
                                    :key="item.ID"
                                >
                                    <!-- we use mousedown instead of click as this is handled before blur that hides menu -->
                                    <RouterLink
                                        ref="itemElements"
                                        :href="datasource.hrefGetter !== undefined ? datasource?.hrefGetter(item) : undefined"
                                        class="dropdown-item py-2 text-wrap"
                                        :class="{ 'eq-form-focus': keyboardActiveItem === item }"
                                        @mousedown="handleItem($event, item as TItem)"
                                    >
                                        <component
                                            v-if="props.displayItemComponent"
                                            :is="props.displayItemComponent"
                                            :item="item as TItem"
                                            :markSearchText="markSearchText"
                                            :prettifyOutput="prettifyOutput"
                                            :searchValueSnapshot="searchValueSnapshot"
                                            :SearchType="datasource.SearchType"
                                        />
                                        <template v-else>
                                            <div
                                                v-html="
                                                    markSearchText(prettifyOutput(item.Text!), datasource.SearchType, searchValueSnapshot)
                                                "
                                            ></div>
                                            <span
                                                v-if="item.Description"
                                                class="small"
                                            >
                                                {{ item.Description }}
                                            </span>
                                        </template>
                                    </RouterLink>
                                </li>
                            </template>
                        </template>
                    </template>
                </ul>
            </template>
        </DropdownComp>
    </div>
</template>
